import { z } from 'zod'
import { createTableSearchSchema } from '@/lib/data-table/search-schema'
import type { TableSearchState } from '@/lib/data-table/types'
import { ADMIN_AUDIT_FILTER_KEYS } from '../components/audit/admin-audit-columns'

const deepLinkSchema = z.object({
  id: z.string().max(100).optional(),
  action: z.string().max(100).optional(),
  actor: z.string().max(200).optional(),
  target_type: z.string().max(50).optional(),
  target_id: z.string().max(100).optional(),
})

/**
 * TableCN search plus ops/flags deep-link aliases (`?id=`, `?actor=`,
 * `?target_type=` + `?target_id=`). Aliases fold into `q` / `filters`.
 */
export const auditSearchSchema = createTableSearchSchema({
  defaultPageSize: 50,
  filterKeys: ADMIN_AUDIT_FILTER_KEYS,
})
  .extend(deepLinkSchema.shape)
  .transform((raw): TableSearchState => {
    const filters: Record<string, string> = { ...(raw.filters ?? {}) }
    const fold = (key: 'id' | 'action' | 'target_type' | 'target_id') => {
      const v = raw[key]?.trim()
      if (v && !filters[key]) filters[key] = v
    }
    fold('id')
    fold('action')
    fold('target_type')
    fold('target_id')
    const actor = raw.actor?.trim()
    return {
      page: raw.page,
      pageSize: raw.pageSize,
      q: raw.q || actor || undefined,
      sort: raw.sort,
      cols: raw.cols,
      selected: raw.selected,
      filters: Object.keys(filters).length ? filters : undefined,
    }
  })

/** Typed `Link` search for the audit table (id / target / action deep-links). */
export function auditDeepLink(opts: {
  id?: string
  action?: string
  actor?: string
  target_type?: string
  target_id?: string
}): TableSearchState {
  const filters: Record<string, string> = {}
  if (opts.id) filters.id = opts.id
  if (opts.action) filters.action = opts.action
  if (opts.target_type) filters.target_type = opts.target_type
  if (opts.target_id) filters.target_id = opts.target_id
  return {
    page: 1,
    pageSize: 50,
    q: opts.actor,
    filters: Object.keys(filters).length ? filters : undefined,
  }
}
